/**
 * PersonalizedRecommendations Component
 *
 * Displays personalized SAT study recommendations based on user's goal and performance.
 * Can be used in read-only mode for coaches/guardians viewing a student's progress.
 */

import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import {
  TrendingUp,
  Target,
  BookOpen,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  ChevronRight,
  Sparkles
} from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'

/**
 * PersonalizedRecommendations - Shows personalized study recommendations
 *
 * @param {Object} props - Component props
 * @param {Object} props.recommendations - Recommendations data from API
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.readOnly - If true, hides action buttons (for coach/guardian views)
 * @param {Function} props.onStartPractice - Callback when user clicks start practice (not used in readOnly mode)
 */
const PersonalizedRecommendations = ({
  recommendations,
  loading = false,
  readOnly = false,
  onStartPractice = null
}) => {
  const navigate = useNavigate()

  const handleStartPractice = () => {
    if (onStartPractice) {
      onStartPractice()
    } else {
      navigate('/challenge-type')
    }
  }

  if (loading) {
    return (
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Personalized Recommendations
          </h2>
        </div>
        <div className="card-body">
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        </div>
      </div>
    )
  }

  // Not enough practice data
  if (recommendations?.status === 'insufficient_data') {
    return (
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Personalized Recommendations
          </h2>
        </div>
        <div className="card-body">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-indigo-100 dark:from-primary-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {readOnly ? 'More Practice Needed' : 'Keep Practicing!'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
              {readOnly
                ? 'More challenges need to be completed to unlock personalized recommendations.'
                : 'Complete more challenges to unlock personalized recommendations based on your performance.'}
            </p>
            {!readOnly && (
              <button onClick={handleStartPractice} className="btn-primary">
                Start a Challenge
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // No recommendations data
  if (!recommendations) {
    return (
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Personalized Recommendations
          </h2>
        </div>
        <div className="card-body">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {readOnly
                ? 'No goal has been set yet.'
                : 'Set a goal to see personalized recommendations'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // On track to meet goal
  if (recommendations.status === 'on_track') {
    return (
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Personalized Recommendations
            </h2>
          </div>
        </div>
        <div className="card-body">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-green-700 dark:text-green-300 mb-2">
              {readOnly ? 'On Track!' : "You're On Track!"}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
              {readOnly ? 'The predicted score of ' : 'Your predicted score of '}
              <span className="font-bold text-green-600 dark:text-green-400">
                {recommendations.predictedScore}
              </span>{' '}
              exceeds the goal of {recommendations.goalScore}.{' '}
              {readOnly ? 'Great progress!' : 'Keep up the great work!'}
            </p>
            {recommendations.recommendations?.length > 0 && (
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-left">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{recommendations.recommendations[0].title}</span>
                </div>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {recommendations.recommendations[0].description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Needs work - show actionable recommendations
  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Personalized Recommendations
          </h2>
        </div>
        {recommendations?.gap > 0 && (
          <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium rounded-full">
            {recommendations.gap} points to goal
          </span>
        )}
      </div>
      <div className="card-body">
        <div className="space-y-4">
          {/* Gap Summary */}
          <div className="p-4 bg-gradient-to-r from-purple-50 to-primary-50 dark:from-purple-900/20 dark:to-primary-900/20 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-medium">Current prediction:</span>{' '}
              <span className="text-primary-600 dark:text-primary-400 font-bold">
                {recommendations.predictedScore}
              </span>
              {' → '}
              <span className="font-medium">Goal:</span>{' '}
              <span className="text-purple-600 dark:text-purple-400 font-bold">
                {recommendations.goalScore}
              </span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {recommendations.message}
            </p>
          </div>

          {/* Weak Areas */}
          {recommendations.weakAreas?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                Areas Needing Focus
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {recommendations.weakAreas.map((area, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                  >
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {area.domain}
                    </span>
                    <span
                      className={`text-sm font-bold ${
                        area.accuracy < 50
                          ? 'text-red-600 dark:text-red-400'
                          : area.accuracy < 70
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-green-600 dark:text-green-400'
                      }`}
                    >
                      {Math.round(area.accuracy)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations List */}
          {recommendations.recommendations?.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary-500" />
                Recommended Actions
              </h4>
              {recommendations.recommendations.map((rec, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    rec.priority === 'high'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      : rec.priority === 'medium'
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        rec.priority === 'high'
                          ? 'bg-red-100 dark:bg-red-900/40'
                          : rec.priority === 'medium'
                            ? 'bg-amber-100 dark:bg-amber-900/40'
                            : 'bg-blue-100 dark:bg-blue-900/40'
                      }`}
                    >
                      {rec.type === 'domain_focus' ? (
                        <Target
                          className={`w-5 h-5 ${
                            rec.priority === 'high'
                              ? 'text-red-600 dark:text-red-400'
                              : rec.priority === 'medium'
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-blue-600 dark:text-blue-400'
                          }`}
                        />
                      ) : rec.type === 'consistency' ? (
                        <TrendingUp
                          className={`w-5 h-5 ${
                            rec.priority === 'high'
                              ? 'text-red-600 dark:text-red-400'
                              : rec.priority === 'medium'
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-blue-600 dark:text-blue-400'
                          }`}
                        />
                      ) : (
                        <BookOpen
                          className={`w-5 h-5 ${
                            rec.priority === 'high'
                              ? 'text-red-600 dark:text-red-400'
                              : rec.priority === 'medium'
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-blue-600 dark:text-blue-400'
                          }`}
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100">
                        {rec.title}
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {rec.description}
                      </p>
                      {rec.potentialGain && (
                        <span className="inline-block mt-2 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                          +{rec.potentialGain} potential points
                        </span>
                      )}
                      {!readOnly && rec.actionType === 'focused_practice' && rec.actionData && (
                        <button
                          onClick={handleStartPractice}
                          className="mt-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                        >
                          {rec.action}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${
                        rec.priority === 'high'
                          ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                          : rec.priority === 'medium'
                            ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200'
                            : 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                      }`}
                    >
                      {rec.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

PersonalizedRecommendations.propTypes = {
  recommendations: PropTypes.shape({
    status: PropTypes.string,
    message: PropTypes.string,
    goalScore: PropTypes.number,
    predictedScore: PropTypes.number,
    gap: PropTypes.number,
    weakAreas: PropTypes.array,
    recommendations: PropTypes.array
  }),
  loading: PropTypes.bool,
  readOnly: PropTypes.bool,
  onStartPractice: PropTypes.func
}

export default PersonalizedRecommendations
